import { DestroyRef, Signal, computed, effect, inject, signal, untracked } from '@angular/core';

export interface WaitOptions {
  grace?: number;
  floor?: number;
  patience?: number;
  /** latch off after the first settle — a cold load can only happen once */
  once?: boolean;
}

export interface WaitState {
  busy: Signal<boolean>;
  visible: Signal<boolean>;
  patient: Signal<boolean>;
  /** a `once` wait that has settled — every later load belongs to a refresh wait */
  spent: Signal<boolean>;
}

export interface WaitTimings {
  grace: number;
  floor: number;
  patience: number;
}

const FALLBACK_TIMINGS: WaitTimings = { grace: 200, floor: 400, patience: 10_000 };

let cachedTimings: WaitTimings | undefined;

function durationToMs(raw: string): number | undefined {
  const value = parseFloat(raw);

  if (!Number.isFinite(value)) {
    return undefined;
  }

  const text = raw.trim();

  if (text.endsWith('ms')) {
    return value;
  }

  return text.endsWith('s') ? value * 1000 : undefined;
}

export function resolveWaitTimings(): WaitTimings {
  if (cachedTimings) {
    return cachedTimings;
  }

  if (typeof document === 'undefined') {
    return FALLBACK_TIMINGS;
  }

  const styles = getComputedStyle(document.documentElement);
  const grace = durationToMs(styles.getPropertyValue('--avyna-wait-grace'));
  const floor = durationToMs(styles.getPropertyValue('--avyna-wait-floor'));
  const patience = durationToMs(styles.getPropertyValue('--avyna-wait-patience'));

  const timings: WaitTimings = {
    grace: grace ?? FALLBACK_TIMINGS.grace,
    floor: floor ?? FALLBACK_TIMINGS.floor,
    patience: patience ?? FALLBACK_TIMINGS.patience,
  };

  // Caching a fallback would pin it past the moment the stylesheet starts to apply.
  if (grace !== undefined && floor !== undefined && patience !== undefined) {
    cachedTimings = timings;
  }

  return timings;
}

export function waitState(source: Signal<boolean>, options: WaitOptions = {}): WaitState {
  const timings = resolveWaitTimings();
  const grace = options.grace ?? timings.grace;
  const floor = options.floor ?? timings.floor;
  const patience = options.patience ?? timings.patience;

  const visible = signal(false);
  const patient = signal(false);
  const spent = signal(false);
  let settled = false;
  let hasBeenBusy = false;

  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let patienceTimer: ReturnType<typeof setTimeout> | undefined;
  let shownAt = 0;

  const clearTimer = (timer: ReturnType<typeof setTimeout> | undefined) => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  };

  inject(DestroyRef).onDestroy(() => {
    clearTimer(showTimer);
    clearTimer(hideTimer);
    clearTimer(patienceTimer);
  });

  effect(() => {
    const isBusy = source();

    untracked(() => {
      if (isBusy) {
        hasBeenBusy = true;

        if (options.once && settled) {
          return;
        }

        clearTimer(hideTimer);
        hideTimer = undefined;

        if (visible() || showTimer !== undefined) {
          return;
        }

        showTimer = setTimeout(() => {
          showTimer = undefined;
          shownAt = Date.now();
          visible.set(true);
          patienceTimer = setTimeout(() => patient.set(true), patience);
        }, grace);

        return;
      }

      settled = settled || hasBeenBusy;
      spent.set(!!options.once && settled);
      clearTimer(showTimer);
      clearTimer(patienceTimer);
      showTimer = undefined;
      patienceTimer = undefined;
      patient.set(false);

      if (!visible()) {
        return;
      }

      hideTimer = setTimeout(
        () => {
          hideTimer = undefined;
          visible.set(false);
        },
        Math.max(0, floor - (Date.now() - shownAt)),
      );
    });
  });

  return {
    busy: computed(() => source()),
    visible: visible.asReadonly(),
    patient: patient.asReadonly(),
    spent: spent.asReadonly(),
  };
}
